<div class="flex">
  <main
    class="flex-1 subnav__exists"
    [ngClass.lt-md]="{ mobile: true, subnav__exists: false }"
    *ngIf="preset$ | async as preset">
    <!-- Loading -->
    <ng-container *ngIf="preset.loading">
      <div class="flex items-center justify-center">
        <mat-spinner diameter="24"></mat-spinner>
      </div>
    </ng-container>

    <!-- Error -->
    <ng-container *ngIf="preset.error">
      <div class="flex flex-col items-center justify-center h-full space-y-4">
        <div class="flex items-center justify-center gap-2">
          <sbnb-icon [icon]="'error'" [size]="24"></sbnb-icon>
          <span>Error fetching thermostat preset</span>
        </div>
        <sbnb-button type="secondary" [routerLink]="['/devices/thermostat-presets']">Go back</sbnb-button>
      </div>
    </ng-container>

    <!-- Success -->
    <div *ngIf="!preset.loading && !preset.error && preset.data" class="flex flex-col space-y-4">
      <!-- Header with editable name -->
      <div class="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-0 sm:mb-4">
        <sbnb-save-indicator
          class="flex-shrink-0 self-end sm:hidden mb-2"
          [saveStatus]="saveStatus"></sbnb-save-indicator>

        <div class="flex items-center gap-2 w-full">
          <sbnb-back-button
            class="mr-4 flex-shrink-0"
            [iconOnly]="true"
            [routerLink]="['/devices/thermostat-presets']"></sbnb-back-button>

          <mat-slide-toggle
            [(ngModel)]="preset.data.is_active"
            [disabled]="preset.data.is_default"
            (ngModelChange)="updatePreset(preset.data)"></mat-slide-toggle>

          <h2 class="input__editable m-0 ml-1 w-full">
            <span
              #nameSpan
              style="padding-left: 0; padding-right: 8px; visibility: hidden; position: absolute; left: -9999px">
              {{ preset.data.name }}
            </span>

            <input
              class="input__borderless"
              type="text"
              [style.width.px]="nameSpan.getBoundingClientRect().width"
              [(ngModel)]="preset.data.name"
              (ngModelChange)="updatePreset(preset.data)" />
          </h2>
        </div>

        <sbnb-save-indicator class="flex-shrink-0 hidden sm:block" [saveStatus]="saveStatus"></sbnb-save-indicator>
      </div>

      <!-- Preset Settings section -->
      <div class="border-t border-smoke py-6">
        <div class="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-6 mb-6">
          <h3 class="!mb-0">Preset settings</h3>
          <span class="text-sm text-night-lighter">
            Set the HVAC mode, heating and cooling setpoints and fan mode of this preset.
          </span>
        </div>

        <!-- Heating set point-->
        <div class="flex gap-2 items-center mt-4">
          <span class="w-40">Heating set point:</span>
          <mat-form-field class="input__mini nopadding w-16">
            <input
              matInput
              type="number"
              inputmode="numeric"
              [min]="THERMOSTAT_TEMPERATURE_LIMITS.MIN_FAHRENHEIT"
              [max]="THERMOSTAT_TEMPERATURE_LIMITS.MAX_FAHRENHEIT"
              [disabled]="preset.data.hvac_mode_setting === 'cool' || preset.data.hvac_mode_setting === 'off'"
              [(ngModel)]="preset.data.heating_set_point_fahrenheit"
              (ngModelChange)="validateAndUpdateTemp($event, 'heating', preset.data)" />
          </mat-form-field>
          <span>&deg;F</span>
        </div>

        <!-- Cooling set point-->
        <div class="flex gap-2 items-center mt-4">
          <span class="w-40">Cooling set point:</span>
          <mat-form-field class="input__mini nopadding w-16">
            <input
              matInput
              type="number"
              inputmode="numeric"
              [min]="THERMOSTAT_TEMPERATURE_LIMITS.MIN_FAHRENHEIT"
              [max]="THERMOSTAT_TEMPERATURE_LIMITS.MAX_FAHRENHEIT"
              [disabled]="preset.data.hvac_mode_setting === 'heat' || preset.data.hvac_mode_setting === 'off'"
              [(ngModel)]="preset.data.cooling_set_point_fahrenheit"
              (ngModelChange)="validateAndUpdateTemp($event, 'cooling', preset.data)" />
          </mat-form-field>
          <span>&deg;F</span>
        </div>

        <!-- HVAC mode -->
        <div class="flex flex-col sm:flex-row gap-2 sm:items-center mt-4">
          <span class="w-full sm:w-40 mb-2 sm:mb-0">HVAC mode:</span>

          <div class="grid grid-cols-2 sm:flex gap-2" data-tour="hvac-mode-buttons">
            <button
              *ngFor="let mode of hvacModes"
              class="mode-button w-full sm:w-auto shrink-0 whitespace-nowrap"
              [ngClass]="{ 'active-mode': preset.data.hvac_mode_setting === mode }"
              (click)="preset.data.hvac_mode_setting = mode; updatePreset(preset.data)">
              {{ mode.toUpperCase() }}
            </button>
          </div>
        </div>

        <!-- Fan mode -->
        <div class="flex flex-col sm:flex-row gap-2 sm:items-center mt-4">
          <span class="w-40">Fan mode:</span>

          <div class="grid grid-cols-2 sm:flex gap-2" [class.disabled]="preset.data.hvac_mode_setting === 'off'">
            <button
              *ngFor="let mode of fanModes"
              class="mode-button"
              [ngClass]="{ 'active-mode': preset.data.fan_mode_setting === mode }"
              (click)="preset.data.fan_mode_setting = mode; updatePreset(preset.data)"
              [disabled]="preset.data.hvac_mode_setting === 'off'">
              {{ mode.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>

      <!-- Preset Type section -->
      <div class="border-t border-smoke py-6">
        <div class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 mb-7">
          <h3 class="!mb-0">Preset type</h3>
          <span class="text-sm text-night-lighter flex-1">
            Select the type of preset to determine when it will be applied. (e.g. before check-in, after check-out)
          </span>
        </div>

        <ul class="space-y-4 list-none p-0 m-0" data-tour="preset-type-list">
          <li *ngFor="let type of presetTypes">
            <sbnb-preset-type
              [preset]="preset.data"
              [type]="type"
              [selected]="(selectedPresetType$ | async) === type.value"
              (update)="updatePreset($event)"></sbnb-preset-type>
          </li>
        </ul>
      </div>

      <!-- Scope section -->
      <div class="border-t border-smoke py-6">
        <div class="flex items-center gap-6 mb-6">
          <h3 class="!mb-0">Scope</h3>
          <span class="text-sm text-night-lighter">Limit usage of this preset to the devices below.</span>
        </div>

        <div class="border border-smoke rounded-lg p-4" data-tour="thermostat-scope-list">
          <ng-container *ngIf="allThermostats$ | async as thermostats; else loadingThermostats">
            <div class="space-y-3 max-h-80 overflow-y-auto">
              <div *ngFor="let thermostat of thermostats" class="flex items-center">
                <mat-checkbox
                  [checked]="thermostat | isThermostatSelected: preset.data.thermostats"
                  (change)="updateThermostatSelection(preset.data, thermostat, $event.checked)"
                  class="text-sm text-night"
                  data-tour="thermostat-scope-item"
                  [disabled]="preset.data.is_default">
                  {{ thermostat.name }}
                </mat-checkbox>
              </div>
            </div>
          </ng-container>
        </div>
      </div>

      <div class="border-t border-smoke py-6 mt-4">
        <div class="flex items-center justify-start gap-4">
          <sbnb-button
            *ngIf="!preset.data.is_default"
            label="Delete"
            icon="delete"
            type="text"
            [labelColor]="'#ED3C6A'"
            [loading]="deleting"
            (clicked)="deletePrompt(preset.data)"></sbnb-button>

          <sbnb-button
            label="Copy"
            icon="copy"
            type="text"
            [labelColor]="'#5c576a'"
            [loading]="copying"
            (clicked)="copyPreset()"></sbnb-button>
        </div>
      </div>
    </div>
  </main>
</div>

<ng-template #loadingThermostats>
  <div class="space-y-4" data-tour="thermostat-scope-loading">
    <div class="flex items-center animate-pulse" *ngFor="let i of [1, 2, 3]">
      <div class="h-6 w-6 rounded bg-gray-200 mr-2"></div>
      <div class="h-5 w-48 rounded bg-gray-200"></div>
    </div>
  </div>
</ng-template>

<sbnb-mobile-sidebar [navTabsHide]="true" title="Thermostat Presets">
  <sbnb-devices-subnav></sbnb-devices-subnav>
</sbnb-mobile-sidebar>
